import { notifications } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { getLogger } from 'loglevel';
import { useCallback } from 'react';

import { ErrorIcon, Success } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { useBackOrNavigate } from '@apple/lib/tanstack-router/hooks/use-router-history';
import { DetailsLayout } from '@apple/ui/layouts/DetailsLayout';
import { ServerValidationError } from '@apple/utils/api/errors';

import { requireAuth } from '@apple/features/auth/utils/require-auth';
import { addUser } from '@apple/features/user-retail/api/management';
import { UserForm } from '@apple/features/user-retail/components/UserForm';
import { userManageQueries } from '@apple/features/user-retail/queries/manage';

const log = getLogger('manage-user');

export const Route = createFileRoute('/_authed/_admin/manage/users_/add')({
	beforeLoad: ({ ...args }) => {
		requireAuth(args, {
			requirePermission: 'Manzanita.Security.Features.UserManagement.Write',
		});

		return {
			userManagementOptionsQueryOptions: userManageQueries.options,
		};
	},
	component: AddUserRoute,
});

function AddUserRoute() {
	const { queryClient } = Route.useRouteContext();
	const { t } = useTranslation('user');
	const goBackToList = useBackOrNavigate();

	const onSuccessfulChange = useCallback(
		async (message: string) => {
			await queryClient.invalidateQueries({ queryKey: ['users-odata'] });
			notifications.show({
				message: message,
				icon: <Success />,
				color: 'green',
				autoClose: 5000,
			});
			await goBackToList('/manage/users');
		},
		[goBackToList, queryClient],
	);

	const onFailedChange = useCallback(
		(error: Error) => {
			if (!(error instanceof ServerValidationError)) {
				log.error(error);
				notifications.show({
					message: t('common:error.generic'),
					icon: <ErrorIcon />,
					color: 'red',
					autoClose: 5000,
				});
			}
		},
		[t],
	);

	const addUserMutation = useMutation({
		mutationFn: addUser,
		onSuccess: async () => {
			await onSuccessfulChange(t('notifications.createSuccess'));
		},
		onError: onFailedChange,
	});

	return (
		<DetailsLayout title={t('addUser.title')}>
			<UserForm saveUserMutation={addUserMutation} />
		</DetailsLayout>
	);
}
